import { useEffect, useLayoutEffect, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

export interface CellEditPopoverProps {
  /** The trigger the editor belongs to — normally the cell's own value button. It stays rendered and untouched. */
  anchor: HTMLElement;
  /** Accessible name for the editor, e.g. "Precio: Mermelaana Fresa 200 g". */
  title: string;
  /** Called on ESC, on a click outside, and by the editor's own cancel button. */
  onCancel(): void;
  children: ReactNode;
}

/** Breathing room between the anchor and the editor, and between the editor and the viewport edge. */
const GAP = 6;
const MARGIN = 8;

/**
 * Editing a single table cell without moving anything.
 *
 * A price editor is an input, a save button, a cancel button and a hint. That
 * cannot fit the footprint of the number it replaces, and the previous attempt
 * to make it fit — rendering all four inside the `<td>` — is exactly the
 * defect reported as OneDev xyntara/equinox #2: the row grew to roughly four
 * times its height and shoved every row below it down the page.
 *
 * ROLES-DESIGNER.md ("Layout stability") names the remedy for precisely this
 * case: when the edit state cannot physically occupy the rest state's box, it
 * becomes an overlay anchored to the element, floating above its neighbours
 * instead of displacing them. That is this component.
 *
 * It renders through a portal into `document.body` rather than inside the
 * cell, and that is load-bearing, not stylistic: `.table-wrap` scrolls
 * horizontally, so a popover positioned inside it would be clipped by that
 * scroll container the moment it reached the last row or the right-hand
 * columns. From the body, with `position: fixed` coordinates recomputed off
 * the anchor's own rectangle, it cannot be clipped by anything — and because
 * the trigger is never unmounted or re-styled, the table's geometry is
 * provably untouched while the editor is open.
 */
export function CellEditPopover({ anchor, title, onCancel, children }: CellEditPopoverProps) {
  const ref = useRef<HTMLDivElement>(null);
  // Read through a ref so the listeners below subscribe once per anchor
  // instead of re-subscribing on every parent render (the parent re-renders on
  // each keystroke in the field this popover contains).
  const cancelRef = useRef(onCancel);
  cancelRef.current = onCancel;

  // Positioned in a layout effect — before the browser paints — so the editor
  // is never visible at the wrong coordinates for even one frame. Written
  // straight onto the node rather than held in state: a state round trip would
  // mean measure → render → measure again, which is how positioning loops
  // start.
  useLayoutEffect(() => {
    const node = ref.current;
    if (!node) return;

    function reposition() {
      if (!node || !anchor.isConnected) return;
      const a = anchor.getBoundingClientRect();
      const { width, height } = node.getBoundingClientRect();
      const vw = document.documentElement.clientWidth;
      const vh = document.documentElement.clientHeight;

      // Right-aligned to the anchor: every cell that gets one of these is a
      // right-aligned numeric column, so the field lines up under the figure
      // it is editing.
      const left = Math.max(MARGIN, Math.min(a.right - width, vw - width - MARGIN));

      // Below by default, above when the row is near the bottom of the
      // viewport — an editor that opens off-screen is a dead control.
      let top = a.bottom + GAP;
      if (top + height > vh - MARGIN) {
        const above = a.top - GAP - height;
        top = above >= MARGIN ? above : Math.max(MARGIN, vh - height - MARGIN);
      }

      node.style.left = `${left}px`;
      node.style.top = `${top}px`;
      node.style.visibility = 'visible';
    }

    reposition();
    // Capture phase: the anchor sits inside `.table-wrap`, whose scroll events
    // do not bubble to window.
    window.addEventListener('scroll', reposition, true);
    window.addEventListener('resize', reposition);
    return () => {
      window.removeEventListener('scroll', reposition, true);
      window.removeEventListener('resize', reposition);
    };
  }, [anchor]);

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        event.stopPropagation();
        cancelRef.current();
      }
    }
    function onPointerDown(event: PointerEvent) {
      const node = ref.current;
      const target = event.target as Node | null;
      if (!node || !target) return;
      if (node.contains(target) || anchor.contains(target)) return;
      cancelRef.current();
    }
    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('pointerdown', onPointerDown, true);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('pointerdown', onPointerDown, true);
    };
  }, [anchor]);

  // Focus moves into the field on open and back to the cell on close, so the
  // keyboard path through the table is never broken. `isConnected` guards the
  // case where the row was re-rendered by a successful save and the original
  // trigger node no longer exists.
  useEffect(() => {
    const first = ref.current?.querySelector<HTMLElement>('input, select, textarea, button');
    first?.focus();
    return () => {
      if (anchor.isConnected) anchor.focus();
    };
  }, [anchor]);

  return createPortal(
    <div
      ref={ref}
      className="cell-edit"
      role="dialog"
      aria-label={title}
      style={{ visibility: 'hidden' }}
    >
      {children}
    </div>,
    document.body,
  );
}
